import RuntimeConfigError from "_shared/runtime-config/runtime-config-error";
import RuntimeConfigSchema, { type RuntimeConfig } from "_shared/runtime-config/runtime-config-schema";

/** Where the page reads its environment from, at the root of the site that serves the bundle. */
export const RUNTIME_CONFIG_PATH = "/config.json";

let installed: RuntimeConfig | undefined;

/**
 * Reads and validates `/config.json` from the site that served the page.
 *
 * Every failure throws a {@link RuntimeConfigError} that says what was wrong, and none falls back.
 * CloudFront and every local server answer an unknown path with `index.html` and a 200, so a
 * missing file arrives as HTML rather than as a 404: the content type is what tells the two apart.
 */
export async function loadRuntimeConfig(origin: string = window.location.origin): Promise<RuntimeConfig> {
  const url = new URL(RUNTIME_CONFIG_PATH, origin).toString();

  let response: Response;
  try {
    // The bundle is cached for good and this file must never be: it is what a deploy changes.
    response = await fetch(url, { cache: "no-store", headers: { Accept: "application/json" } });
  } catch (error) {
    throw new RuntimeConfigError(`${url} could not be fetched.`, { cause: error });
  }

  if (!response.ok) {
    throw new RuntimeConfigError(`${url} answered ${response.status} ${response.statusText}.`.trimEnd());
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("json")) {
    throw new RuntimeConfigError(
      `${url} is missing: the server answered with ${contentType || "no content type"}, not JSON. ` +
        "The deploy writes this file for its environment.",
    );
  }

  let values: unknown;
  try {
    values = await response.json();
  } catch (error) {
    throw new RuntimeConfigError(`${url} is not valid JSON.`, { cause: error });
  }

  return RuntimeConfigSchema.validate(values, url);
}

/** Makes a loaded configuration the one {@link runtimeConfig} answers. The entry point calls it once. */
export function installRuntimeConfig(config: RuntimeConfig): void {
  installed = config;
}

/**
 * The configuration the entry point loaded. `meta.ts` reads it when it is first imported, which is
 * why `index.tsx` imports the application only after {@link installRuntimeConfig}.
 */
export function runtimeConfig(): RuntimeConfig {
  if (installed === undefined) {
    throw new RuntimeConfigError(
      `The configuration was read before ${RUNTIME_CONFIG_PATH} was loaded. index.tsx loads it before it imports the application; ` +
        "a module that reads it must not be imported ahead of that.",
    );
  }
  return installed;
}
